import { useMemo, useState } from 'react';
import { useStudioStore } from '@/store/useStudioStore';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { X, ChevronDown, Check, Clock, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 5;

const getHistoryStatusLabel = (status: string) => {
  if (status === 'review') return 'Needs review';
  if (status === 'dismissed') return 'Kept current';
  if (status === 'pending') return 'In progress';
  return 'Applied';
};

const getHistoryDetailSections = (detailText: string) => {
  const raw = String(detailText || '')
    .replace(/\r/g, '')
    .trim();
  if (!raw) return [];

  const lines = raw.split('\n');
  const sections: Array<{ label: string; body: string }> = [];
  let current: { label: string; lines: string[] } | null = null;

  const pushCurrentSection = () => {
    if (!current) return;
    const body = current.lines
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    if (!body) return;
    sections.push({
      label: current.label || 'Summary',
      body,
    });
    current = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      if (current && current.lines.length && current.lines[current.lines.length - 1] !== '') {
        current.lines.push('');
      }
      return;
    }

    const labelMatch = trimmed.match(/^([A-Z][A-Za-z ]{1,32}):\s*(.*)$/);
    if (labelMatch) {
      pushCurrentSection();
      current = {
        label: labelMatch[1],
        lines: labelMatch[2] ? [labelMatch[2]] : [],
      };
      return;
    }

    if (!current) {
      current = {
        label: 'Summary',
        lines: [],
      };
    }

    current.lines.push(trimmed);
  });

  pushCurrentSection();

  if (!sections.length) {
    return [
      {
        label: 'Summary',
        body: raw,
      },
    ];
  }

  return sections;
};

const getHistoryPreviewText = (detail: string, title: string) => {
  const sections = getHistoryDetailSections(detail || title);
  const previewSource = sections.length ? sections[0].body : String(title || '');
  return previewSource.replace(/\s+/g, ' ').trim();
};

const renderHistorySectionCards = (detail: string) => {
  const sections = getHistoryDetailSections(detail);

  return sections.map((section, index) => (
    <div
      key={`${section.label}-${index}`}
      className="grid gap-[8px] rounded-[16px] border border-[rgba(148,163,184,0.16)] bg-[rgba(247,250,255,0.96)] px-[13px] py-[12px]"
    >
      <div className="text-[10.5px] leading-none font-bold tracking-[0.08em] text-[rgba(29,78,216,0.72)] uppercase">
        {section.label}
      </div>
      <div className="text-[12.25px] leading-[1.68] whitespace-pre-line text-[rgba(15,23,42,0.72)]">
        {section.body}
      </div>
    </div>
  ));
};

const HistIcon = ({ status }: { status: string }) => {
  if (status === 'dismissed') return <RotateCcw size={14} />;
  if (status === 'review') return <Clock size={14} />;
  if (status === 'pending') return <Clock size={14} />;
  return <Check size={14} />;
};

export const StudioHistoryListModal = () => {
  const { isHistoryListModalOpen, setHistoryListModalOpen, historyItems } = useStudioStore();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setPage(1);
      setExpandedId(null);
    }
    setHistoryListModalOpen(open);
  };

  const sortedEntries = useMemo(() => historyItems, [historyItems]);
  const total = sortedEntries.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, total);
  const pageItems = sortedEntries.slice(startIndex, endIndex);
  const latestEntry = sortedEntries[0];
  const appliedCount = sortedEntries.filter((item) => item.status === 'done').length;

  return (
    <Dialog open={isHistoryListModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="grid max-h-[min(86vh,780px)] w-[min(820px,100%)] grid-rows-[auto_auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-[28px] border border-[rgba(37,99,235,0.14)] bg-[linear-gradient(180deg,rgba(249,252,255,0.99)_0%,rgba(243,247,253,0.99)_100%)] p-0 shadow-[0_34px_88px_rgba(15,23,42,0.26)] ring-0"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <div className="flex items-start justify-between gap-4 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.14),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(245,248,255,0.9)_100%)] px-6 pt-[22px] pb-4">
          <div className="grid min-w-0 gap-1">
            <div className="text-[11px] leading-none font-bold tracking-[0.14em] text-[rgba(29,78,216,0.7)] uppercase">
              Recent Changes
            </div>
            <DialogTitle className="text-[22px] leading-[1.2] font-bold tracking-[-0.02em] text-[#0F172A]">
              Recent changes
            </DialogTitle>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-[14px] border border-[rgba(148,163,184,0.18)] bg-[rgba(255,255,255,0.88)] text-[rgba(15,23,42,0.46)] transition-all hover:border-[rgba(37,99,235,0.26)] hover:bg-[rgba(255,255,255,0.98)] hover:text-[#2563EB]"
            >
              <X size={16} />
            </button>
          </DialogClose>
        </div>

        <div className="grid grid-cols-2 gap-[10px] border-b border-[rgba(148,163,184,0.14)] bg-[linear-gradient(180deg,rgba(247,250,255,0.96)_0%,rgba(244,247,253,0.84)_100%)] px-6 pb-[18px]">
          <div className="grid gap-[5px] rounded-[18px] border border-[rgba(148,163,184,0.18)] bg-[rgba(255,255,255,0.86)] px-[13px] py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
            <span className="text-[10.5px] leading-none font-bold tracking-[0.08em] text-[rgba(15,23,42,0.46)] uppercase">
              Latest update
            </span>
            <span className="min-w-0 text-[12.5px] leading-[1.5] font-semibold text-[#0F172A]">
              {latestEntry ? latestEntry.at : 'No changes yet'}
            </span>
          </div>
          <div className="grid gap-[5px] rounded-[18px] border border-[rgba(148,163,184,0.18)] bg-[rgba(255,255,255,0.86)] px-[13px] py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
            <span className="text-[10.5px] leading-none font-bold tracking-[0.08em] text-[rgba(15,23,42,0.46)] uppercase">
              Applied
            </span>
            <span className="min-w-0 text-[12.5px] leading-[1.5] font-semibold text-[#0F172A]">
              {appliedCount}
            </span>
          </div>
        </div>

        <div className="min-h-0 overflow-auto bg-[linear-gradient(180deg,rgba(249,251,255,0.9)_0%,rgba(244,247,252,0.94)_100%)] px-6 pt-[22px] pb-6">
          {total === 0 ? (
            <div className="grid gap-[14px]">
              <div className="rounded-[20px] border border-dashed border-[rgba(148,163,184,0.2)] bg-[rgba(255,255,255,0.8)] px-[22px] py-[28px] text-center text-[12.5px] leading-[1.72] text-[rgba(15,23,42,0.54)]">
                No changes yet. Start a chat request or run Expert Review to generate the first
                activity record for this draft.
              </div>
            </div>
          ) : (
            <div className="grid gap-[14px]">
              <section className="grid gap-[10px] rounded-[22px] border border-[rgba(148,163,184,0.18)] bg-[rgba(255,255,255,0.84)] shadow-[inset_0_1px_0_rgba(255,255,255,0.92)]">
                <div className="grid gap-[10px]">
                  {pageItems.map((item) => {
                    const isExpanded = expandedId === item.id;
                    const previewText = getHistoryPreviewText(item.detail, item.title);

                    return (
                      <button
                        key={item.id}
                        type="button"
                        data-status={item.status}
                        className={cn(
                          'font-inherit block w-full rounded-[20px] border border-[rgba(148,163,184,0.18)] bg-[rgba(255,255,255,0.94)] px-4 py-[15px] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.88)] transition-all',
                          'hover:-translate-y-px hover:border-[rgba(37,99,235,0.24)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.08)]',
                          isExpanded &&
                            'border-[rgba(37,99,235,0.26)] bg-[rgba(255,255,255,0.98)] shadow-[0_16px_32px_rgba(37,99,235,0.08)]',
                        )}
                        onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      >
                        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-[14px]">
                          <div
                            className={cn(
                              'mt-[1px] inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] border border-[rgba(148,163,184,0.18)] bg-[rgba(241,245,251,0.92)] text-[rgba(15,23,42,0.54)] shadow-[inset_0_1px_0_rgba(255,255,255,0.88)]',
                              item.status === 'done' &&
                                'border-[rgba(5,150,105,0.18)] bg-[rgba(236,253,245,0.92)] text-[#059669]',
                              item.status === 'dismissed' &&
                                'border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.94)] text-[rgba(100,116,139,0.76)]',
                              item.status === 'review' &&
                                'border-[rgba(8,145,178,0.16)] bg-[rgba(236,254,255,0.94)] text-[#0891B2]',
                              item.status === 'pending' &&
                                'border-[rgba(37,99,235,0.18)] bg-[rgba(239,246,255,0.94)] text-[#2563EB]',
                            )}
                          >
                            <HistIcon status={item.status} />
                          </div>

                          <div className="grid min-w-0 gap-2">
                            <div className="text-[14px] leading-[1.5] font-semibold text-[#0F172A]">
                              {item.title}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={cn(
                                  'rounded-full px-[8px] py-[5px] text-[10.5px] leading-none font-bold tracking-[0.04em] uppercase',
                                  item.status === 'done' &&
                                    'bg-[rgba(5,150,105,0.11)] text-[#059669]',
                                  item.status === 'dismissed' &&
                                    'bg-[rgba(148,163,184,0.16)] text-[rgba(71,85,105,0.9)]',
                                  item.status === 'review' &&
                                    'bg-[rgba(8,145,178,0.12)] text-[#0891B2]',
                                  item.status === 'pending' &&
                                    'bg-[rgba(37,99,235,0.1)] text-[#2563EB]',
                                )}
                              >
                                {getHistoryStatusLabel(item.status)}
                              </span>
                              <span className="text-[11px] leading-[1.3] font-medium text-[rgba(15,23,42,0.5)]">
                                {item.source}
                              </span>
                              <span className="text-[11px] leading-[1.3] text-[rgba(15,23,42,0.46)]">
                                {item.at}
                              </span>
                            </div>
                            <div className="line-clamp-2 text-[12.5px] leading-[1.68] text-[rgba(15,23,42,0.6)]">
                              {previewText}
                            </div>
                          </div>

                          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[11px] border border-[rgba(148,163,184,0.16)] bg-[rgba(241,245,251,0.76)] text-[rgba(15,23,42,0.42)]">
                            <ChevronDown
                              size={11}
                              className={cn(
                                'transition-transform duration-200',
                                isExpanded && 'rotate-180 text-[#2563EB]',
                              )}
                            />
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-[14px] grid gap-[10px] border-t border-[rgba(148,163,184,0.18)] pt-[14px]">
                            <div className="grid gap-[10px]">
                              {renderHistorySectionCards(item.detail || item.title)}
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="mt-[4px] flex items-center justify-between gap-[12px] px-[14px] pb-[14px]">
                    <div className="text-[11.5px] text-[rgba(15,23,42,0.5)]">
                      Showing {startIndex + 1}-{endIndex} of {total}
                    </div>
                    <div className="inline-flex gap-[8px]">
                      <button
                        type="button"
                        className="min-h-[34px] rounded-[11px] border border-[rgba(148,163,184,0.2)] bg-[rgba(255,255,255,0.9)] px-3 text-[11.5px] font-semibold text-[rgba(15,23,42,0.68)] transition-all hover:border-[rgba(37,99,235,0.28)] hover:bg-[rgba(239,246,255,0.92)] hover:text-[#2563EB] disabled:cursor-default disabled:opacity-45"
                        disabled={safePage === 1}
                        onClick={() => {
                          setPage(safePage - 1);
                          setExpandedId(null);
                        }}
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        className="min-h-[34px] rounded-[11px] border border-[rgba(148,163,184,0.2)] bg-[rgba(255,255,255,0.9)] px-3 text-[11.5px] font-semibold text-[rgba(15,23,42,0.68)] transition-all hover:border-[rgba(37,99,235,0.28)] hover:bg-[rgba(239,246,255,0.92)] hover:text-[#2563EB] disabled:cursor-default disabled:opacity-45"
                        disabled={safePage === totalPages}
                        onClick={() => {
                          setPage(safePage + 1);
                          setExpandedId(null);
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
